import { StrictMode, startTransition } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { StartClient } from '@tanstack/react-start/client';
import { errorReportData, extractErrorInfo, logErrorFn } from './lib/data';
import { isHydrationError, shouldReportFrontendError } from './lib/frontend-error-filter';
import { describeHydrationRewrite, markHydrationStarted } from './lib/hydration-rewrite-monitor';
import { clientQueryClient } from './router';
import { recentBreadcrumbs } from './lib/session-actions';
import { documentReplayed } from './lib/ssr-trace';
import { isVersionSkewReloadInProgress } from './routes/-components/default-catch-boundary';
import {
	COUNTER_LOCALE_ATTR,
	COUNTER_MODE_ATTR,
	COUNTER_MODE_ENTRANCE,
	COUNTER_VALUE_ATTR,
} from '@worldcoo/ui/lib/counter-contract';
import type { ClientErrorSource } from '@worldcoo/monolith/wide-events';

/**
 * Exists only to give `hydrateRoot` error handlers that keep React's `componentStack`; otherwise a
 * verbatim copy of the default entry. docs/notes/portals/hydration-error-reporting.md
 */

/** `entrance` renders zero on both sides, so it is the one mode worth dropping at the cap. */
const modeRank = (el: HTMLElement): number => (el.getAttribute(COUNTER_MODE_ATTR) === COUNTER_MODE_ENTRANCE ? 1 : 0);

/**
 * Names the text a production #418 will not. A hydrated node's `data-value` is the SERVER's figure, the
 * opposite of the obvious reading (TEC-3758). docs/notes/portals/hydration-error-reporting.md
 */
function counterSnapshot(): string | undefined {
	if (typeof document === 'undefined') return undefined;
	try {
		// Suspense holding containers keep the server's stale copy; skipped before the cap (TEC-3804).
		const nodes = Array.from(document.querySelectorAll<HTMLElement>(`[${COUNTER_VALUE_ATTR}]`))
			.filter((el) => !el.closest('div[hidden][id^="S:"]'))
			.sort((a, b) => modeRank(a) - modeRank(b))
			.slice(0, 10);
		if (nodes.length === 0) return undefined;
		// Like `data-value`, the locale attribute names the side that formatted the node (TEC-3696).
		const shellLocale = document.documentElement.lang || '?';
		const lines = nodes.map(
			(el) =>
				`${el.getAttribute(COUNTER_MODE_ATTR) ?? '?'}/${el.getAttribute(COUNTER_LOCALE_ATTR) ?? '?'}/${el.dataset.testid || '-'}:${el.getAttribute(COUNTER_VALUE_ATTR) ?? '?'}=>${(el.textContent ?? '').trim().slice(0, 24)}`,
		);
		return [`lang:${shellLocale}`, ...lines].join(' | ');
	} catch {
		return undefined;
	}
}

/** Replaces React's `reportError` call, so each error is reported once, with its component stack. */
function reportWithComponentStack(
	resolveKind: (message: string) => ReportKind,
	error: unknown,
	errorInfo: { componentStack?: string | null },
): void {
	// A symptom of the version skew a reload is already fixing.
	if (isVersionSkewReloadInProgress()) return;

	const info = extractErrorInfo(error);
	const { prefix, source } = resolveKind(info.message);
	console.error(prefix, info.name, info.message, errorInfo.componentStack);

	// A rewritten document's mismatch is the rewriter's; the same gate as `router.tsx`, failing open.
	const rewrite = isHydrationError(info.message) ? describeHydrationRewrite(clientQueryClient()) : undefined;
	if (rewrite?.foreign) return;

	if (!shouldReportFrontendError(info.name, info.message, info.stack, info.cause)) return;

	const componentStack = errorInfo.componentStack?.trim();
	logErrorFn({
		data: errorReportData(prefix + ' ', info, {
			stack: componentStack ? `${info.stack ?? '(no stack)'}\n\nComponent stack:${componentStack}` : info.stack,
			source,
			breadcrumbs: recentBreadcrumbs(),
			// Attribution first: the field is cut from the tail at 600 chars.
			counters: [rewrite?.attribution, counterSnapshot()].filter(Boolean).join(' || ') || undefined,
			// React reports `args[]=text` with an empty diff in production.
			mismatch: rewrite?.mismatch,
			// Absent means never asked, `false` asked and clean (TEC-3854, TEC-3881).
			documentReplayed: isHydrationError(info.message) ? documentReplayed() : undefined,
		}),
	}).catch((logErr) => {
		console.error('Failed to log error to backend:', logErr);
	});
}

/** The log prefix and the telemetry `source` are one decision, so they travel together. */
interface ReportKind {
	prefix: string;
	source: ClientErrorSource;
}

function reportRecoverableError(error: unknown, errorInfo: { componentStack?: string | null }): void {
	reportWithComponentStack(
		(message) =>
			isHydrationError(message)
				? { prefix: '[HYDRATION]', source: 'hydration' }
				: { prefix: '[RECOVERABLE]', source: 'recoverable' },
		error,
		errorInfo,
	);
}

/** Its own prefix, so a broken page never hides behind a cosmetic-mismatch ticket (TEC-3690). */
function reportUncaughtError(error: unknown, errorInfo: { componentStack?: string | null }): void {
	reportWithComponentStack(() => ({ prefix: '[UNCAUGHT]', source: 'uncaught' }), error, errorInfo);
}

// Outside the transition and before it: every rewrite the monitor logs from here on could be React's
// own, and one logged earlier provably could not. See `HYDRATION_STARTED_KEY`.
markHydrationStarted();

startTransition(() => {
	hydrateRoot(
		document,
		<StrictMode>
			<StartClient />
		</StrictMode>,
		// Not `onCaughtError`: `DefaultCatchBoundary` already reports what it caught.
		{ onRecoverableError: reportRecoverableError, onUncaughtError: reportUncaughtError },
	);
});
